import {Component, EnvironmentInjector, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {
  ConfigService,
  FetchAndStoreRecordLocatorResultsInVaultProfile,
  LighthouseBrandListDisplayItem,
  LighthouseEndpointListDisplayItem,
  LighthousePortalListDisplayItem,
  ProcessTefcaDirectAuthorizationResults,
  SourceCredentialType,
} from '@shared-library';
import {FastenService} from '../../services/fasten.service';

@Component({
    selector: 'app-health-system-search',
    templateUrl: './health-system-search.component.html',
    styleUrls: ['./health-system-search.component.css'],
    standalone: false
})
export class HealthSystemSearchComponent implements OnInit {
  loading = false;
  errorMessage = '';
  emptyTefcaResults = false;
  authorizingVaultConnectionId = '';

  constructor(
    public fastenService: FastenService,
    public configService: ConfigService,
    public router: Router,
    private logger: NGXLogger,
    private injector: EnvironmentInjector,
  ) { }

  ngOnInit(): void {
    if (this.configService.systemConfig$.tefcaMode && !this.configService.vaultProfileConfig$.rlsQueryComplete) {
      this.loadDiscoveredProviders();
    }
  }

  loadDiscoveredProviders(forceReload = false): void {
    if (this.loading) {
      return;
    }
    if (!forceReload && this.configService.vaultProfileConfig$.rlsQueryComplete) {
      this.emptyTefcaResults = this.getAvailableAccountCount() === 0;
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.emptyTefcaResults = false;

    FetchAndStoreRecordLocatorResultsInVaultProfile(
      this.configService,
      () => this.fastenService.recordLocatorRegisterAndPollForStatus(),
      (taskId: string) => this.fastenService.recordLocatorResults(taskId),
    ).subscribe({
      next: (totals) => {
        this.emptyTefcaResults = (totals.numDiscovered + totals.numPending + totals.numConnected) === 0;
        this.loading = false;
      },
      error: (error) => {
        this.logger.error('Error fetching TEFCA-discovered providers', error);
        this.configService.vaultProfileConfig = {
          rlsQueryComplete: true
        };
        this.errorMessage = 'We could not load your TEFCA-discovered providers right now. Please try again.';
        this.emptyTefcaResults = true;
        this.loading = false;
      }
    });
  }

  connectDiscoveredAccount(
    vaultProfileConnectionId: string,
    account: {
      brand: LighthouseBrandListDisplayItem,
      portal: LighthousePortalListDisplayItem,
      endpoint: LighthouseEndpointListDisplayItem,
      vault_profile_connection_id?: string
    },
  ): void {
    if (this.authorizingVaultConnectionId) {
      return;
    }

    this.authorizingVaultConnectionId = vaultProfileConnectionId;
    this.errorMessage = '';

    this.configService.vaultProfileAddConnectedAccount({
      external_state: vaultProfileConnectionId,
      org_connection_id: '',
      connection_status: 'connected',
      platform_type: account.endpoint?.platform_type || 'tefca',
      brand_id: account.brand?.id,
      portal_id: account.portal?.id,
      endpoint_id: account.endpoint?.id,
      vault_profile_connection_id: vaultProfileConnectionId,
      patient_auth_type: SourceCredentialType.SourceCredentialTypeTefcaDirect,
    });

    this.fastenService.authorizeTefcaDirect(
      [vaultProfileConnectionId],
      this.configService.systemConfig$.externalId
    ).subscribe({
      next: (resp) => {
        this.injector.runInContext(() => {
          ProcessTefcaDirectAuthorizationResults([vaultProfileConnectionId], resp);
        });
        this.authorizingVaultConnectionId = '';
        this.router.navigateByUrl('dashboard/accounts');
      },
      error: (error) => {
        this.logger.error('Failed to authorize TEFCA provider', error);
        this.injector.runInContext(() => {
          ProcessTefcaDirectAuthorizationResults([vaultProfileConnectionId], null);
        });
        this.errorMessage = 'We could not connect that provider right now. Please try again.';
        this.authorizingVaultConnectionId = '';
      }
    });
  }

  connectPendingAccount(
    externalState: string,
    pendingAccount: {
      brand: LighthouseBrandListDisplayItem,
      portal: LighthousePortalListDisplayItem,
      endpoint: LighthouseEndpointListDisplayItem,
      vault_profile_connection_id?: string
    }
  ): void {
    this.router.navigate(['/brand/connecting'], {
      queryParams: {
        brandId: pendingAccount.brand?.id,
        portalId: pendingAccount.portal?.id,
        endpointId: pendingAccount.endpoint?.id,
        externalId: this.configService.systemConfig$.externalId,
        externalState,
        vaultProfileConnectionId: pendingAccount.vault_profile_connection_id,
      }
    });
  }

  private getAvailableAccountCount(): number {
    const vaultProfile = this.configService.vaultProfileConfig$;
    return Object.keys(vaultProfile.discoveredPatientAccounts || {}).length
      + Object.keys(vaultProfile.pendingPatientAccounts || {}).length;
  }
}
